import {Fragment} from 'react';
import {useEffect, useState} from 'react';
import {UnsortableTable} from '../UnsortableTable';
import {listData} from '../../../constants/listData';
import {TenantProps} from '../../../constants/types';
import Error from '../Error';
import Spinner from '../Spinner';
import {Button} from '../Button';
import {sendGetRequest} from '../../../api/requests';
import ConfirmationPopup from '../popup/ConfirmationPopup';
import ListPopup from '../popup/ListPopup';
import EnsurePermissions from '../EnsurePermission';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../../hooks/useSnackbar';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import IconButton from '@mui/material/IconButton';

type AddToGroupProps = {
  id: string;
  name: string;
  entityKey: string;
}

const AddToGroup = ({id, name, entityKey}: AddToGroupProps) => {
  const {t} = useTranslation('user');
  const [data, setData] = useState<Nullable<TenantProps[]>>(null);
  const [error, setError] = useState('');
  const [isPopupShown, setIsPopupShown] = useState(false);
  const [isListPopupShown, setIsListPopupShown] = useState(false);
  const [isEditPopupShown, setIsEditPopupShown] = useState(false);
  const [getGroupId, setGroupId] = useState<number>();
  const [getGroupName, setGroupName] = useState<string>('');
  const [groupsToAdd, setGroupsToAdd] = useState<number[]>([]);
  const [tenantsToAdd, setTenantsToAdd] = useState<number[]>([]);
  const [groupToRemove, setGroupToRemove] = useState({id: '', name: ''});
  const [spinnerText, setSpinnerText] = useState('');
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const entity = listData[entityKey];

  useEffect(() => {
    (async () => {
      setSpinnerText(entity?.loadingText);
      try {
        const response = await sendGetRequest(entity?.fetchUrl(id));
        setData(response);
      } catch (error) {
        if (error instanceof ErrorEvent) {
          setError(error?.message || '');
        }
      }
    })();
    return () => {
      setError('');
    };
  }, []);

  const handleGroupRemove = async (tenantId: string, tenantName: string) => {
    setSpinnerText(entity?.spinnerTextRemove(tenantName));
    setData(null);
    setIsPopupShown(false);
    try {
      await entity?.remove(id, tenantId);
      addSuccessToast(t('toasts.removeFromGroupMessage'));
      const response = await sendGetRequest(entity?.fetchUrl(id));
      setData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  const handleGroupAdd = async () => {
    setSpinnerText(entity?.spinnerTextAdd(name));
    setData(null);
    setIsListPopupShown(false);
    try {
      await entity?.add(id, groupsToAdd);
      addSuccessToast(t('toasts.addToGroupMessage'));
      setGroupsToAdd([]);
      const response = await sendGetRequest(entity?.fetchUrl(id));
      setData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  const handleTenantAdd = async () => {
    setSpinnerText(entity?.spinnerTextAddTenant(getGroupName));
    setData(null);
    setIsEditPopupShown(false);
    try {
      await entity?.addTenantsToUserGroup(id, getGroupId, tenantsToAdd);
      addSuccessToast(t('toasts.updateTenantsMessage'));
      setTenantsToAdd([]);
      const response = await sendGetRequest(entity?.fetchUrl(id));
      setData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  if (error) {
    return <Error message={error} />;
  }

  const tableContent = !data ?
    <Spinner loadingText={spinnerText} /> :
    <>
      <UnsortableTable
        entity={entity}
        content={data}
        emptyTableMsg={entity?.emptyMessage(name)}>
        {data.map(({id, name, tenants}) => (
          <Fragment key={`add-group-row_${id}`}>
            {entity.isSysConf(location.pathname) ?
            <EnsurePermissions elementPermissions={entity.permissions}>
              <td className="cell narrow-cell">
                <IconButton
                  className="icon-button small blue"
                  onClick={() => {
                    setGroupId(id);
                    setGroupName(name);
                    if (tenants?.length) {
                      setTenantsToAdd(tenants.map(({id}) => id));
                    }
                    setIsEditPopupShown(true);
                  }}
                >
                  <ModeEditIcon className="icon" fontSize="small" />
                </IconButton>
              </td>
            </EnsurePermissions> :
             null}
            <td className="cell remove-btn-cell">
              <EnsurePermissions elementPermissions={entity.permissions}>
                <Button
                  size="small"
                  label="Remove"
                  variant="text"
                  onClick={() => {
                    setGroupToRemove({id: `${id}`, name: name});
                    setIsPopupShown(true);
                  }}
                />
              </EnsurePermissions>
            </td>
          </Fragment>
        ))}
      </UnsortableTable>

      <ConfirmationPopup
        labels={{body: entity?.popupLabel(groupToRemove.name, name)}}
        isOpen={isPopupShown}
        setIsOpen={setIsPopupShown}
        confirmableAction={
          () => handleGroupRemove(groupToRemove.id, groupToRemove.name)
        }
      />

      <ListPopup
        isOpen={isListPopupShown}
        setIsOpen={setIsListPopupShown}
        confirmableAction={handleGroupAdd}
        entityName={name}
        entityKey={entity?.popupEntityKey}
        entitiesToAdd={groupsToAdd}
        setEntitiesToAdd={setGroupsToAdd}
        entitiesList={data}
      />

      {isEditPopupShown &&
      <ListPopup
        isOpen={isEditPopupShown}
        setIsOpen={setIsEditPopupShown}
        confirmableAction={handleTenantAdd}
        entityName={getGroupName}
        entityKey={'list-popup/users/groups/available-tenants'}
        entitiesToAdd={tenantsToAdd}
        setEntitiesToAdd={setTenantsToAdd}
        customRoute={`/users/${id}/groups/${getGroupId}/available-tenants`}
      />}
    </>;

  return (
    <div className="tab-content">
      <div className="tab-header-and-add-btn">
        <h3 className="tab-header">{entity?.header}</h3>

        {data &&
        <EnsurePermissions elementPermissions={entity.permissions}>
          <Button
            label={entity?.addButtonLabel}
            onClick={() => setIsListPopupShown(true)}
          />
        </EnsurePermissions>
        }
      </div>
      {tableContent}
    </div>
  );
};

export default AddToGroup;
